/**
 * /weather/community — full community weather reports feed.
 * Filters by type + severity + hours-old, grouped in a scrollable grid.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import {
  ArrowLeft, Camera, CloudRain, Sun, Snowflake, Wind, Cloud, CloudLightning,
  MapPin, ShieldCheck, ThumbsUp, Loader2,
} from "lucide-react";

const REPORT_ICON = {
  heavy_rain: CloudRain, flooding: CloudRain, flash_flood: CloudRain,
  thunderstorm: CloudLightning, lightning: CloudLightning,
  snow: Snowflake, heavy_snow: Snowflake, blizzard: Snowflake, ice_storm: Snowflake,
  hail: Snowflake, wildfire: Sun, smoke: Sun, extreme_heat: Sun, heat_wave: Sun,
  tornado: Wind, hurricane: Wind, cyclone: Wind, high_wind: Wind, dust_storm: Wind,
  fog: Cloud, downed_tree: Wind, landslide: CloudRain, extreme_cold: Snowflake,
};
const SEVERITY_COLOR = {
  mild: "#22c55e", moderate: "#f59e0b", severe: "#f97316", critical: "#dc2626",
};
const TYPE_TABS = [
  { key: "",            label: "All" },
  { key: "heavy_rain",  label: "Heavy Rain" },
  { key: "flooding",    label: "Flooding" },
  { key: "thunderstorm",label: "Thunderstorm" },
  { key: "snow",        label: "Snow" },
  { key: "hail",        label: "Hail" },
  { key: "high_wind",   label: "High Wind" },
  { key: "wildfire",    label: "Wildfire" },
  { key: "smoke",       label: "Smoke" },
  { key: "fog",         label: "Fog" },
  { key: "extreme_heat",label: "Extreme Heat" },
  { key: "extreme_cold",label: "Extreme Cold" },
];
const _titleize = (s) => String(s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const _ago = (iso) => {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
};

export default function WeatherCommunity() {
  const [type,     setType]     = useState("");
  const [hours,    setHours]    = useState(168);   // 7 days
  const [verified, setVerified] = useState(false);
  const [items,    setItems]    = useState([]);
  const [busy,     setBusy]     = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      params.set("hours", String(hours));
      if (type) params.set("type", type);
      if (verified) params.set("verified_only", "true");
      params.set("limit", "60");
      const { data } = await http.get(`/weather-reports?${params.toString()}`);
      setItems(data.reports || []);
    } catch {
      setItems([]);
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [type, hours, verified]);

  return (
    <div className="max-w-6xl mx-auto px-6 py-10" data-testid="weather-community-page">
      <Link to="/weather" className="inline-flex items-center gap-1 text-sm opacity-70 hover:opacity-100 mb-3">
        <ArrowLeft className="w-3 h-3" /> Weather Almanac
      </Link>
      <div className="overline mb-1" style={{ color: "var(--wofy-primary)" }}>COMMUNITY REPORTS</div>
      <h1 className="font-display text-4xl sm:text-5xl font-bold" style={{ color: "var(--wofy-primary)" }}>
        Real conditions, right now
      </h1>
      <p className="mt-3 max-w-2xl opacity-80">
        Every report is submitted by a real person in that place. Verify a
        neighbour to help others trust the map. Three confirmations turn any
        report <em>verified</em>.
      </p>

      {/* Filters */}
      <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="wc-filters">
        <div className="inline-flex rounded-full border p-0.5" style={{ borderColor: "var(--wofy-border)" }}>
          {[24, 72, 168, 720].map((h) => (
            <button key={h} type="button" onClick={() => setHours(h)}
                    data-testid={`wc-hours-${h}`}
                    className={`text-xs px-3 py-1 rounded-full ${hours === h ? "bg-black text-white" : "hover:bg-black/5"}`}>
              {h === 24 ? "24h" : h === 72 ? "3d" : h === 168 ? "7d" : "30d"}
            </button>
          ))}
        </div>
        <label className="text-xs inline-flex items-center gap-1 opacity-80">
          <input type="checkbox" checked={verified} onChange={(e) => setVerified(e.target.checked)}
                 data-testid="wc-verified-only" />
          Verified only
        </label>
        <Link to="/weather#community-reports" className="ml-auto wofy-btn-primary text-sm"
              data-testid="wc-add">
          <Camera className="w-4 h-4" /> Add a report
        </Link>
      </div>

      <div className="mt-3 flex flex-wrap gap-1.5" data-testid="wc-types">
        {TYPE_TABS.map((t) => (
          <button key={t.key || "all"} type="button" onClick={() => setType(t.key)}
                  data-testid={`wc-type-${t.key || "all"}`}
                  className={`text-xs px-3 py-1 rounded-full border ${type === t.key ? "bg-black text-white" : "hover:bg-black/5"}`}
                  style={type === t.key ? undefined : { borderColor: "var(--wofy-border)" }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Feed */}
      {busy && (
        <div className="mt-6 wofy-card !p-6 inline-flex items-center gap-2" data-testid="wc-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading reports…
        </div>
      )}
      {!busy && items.length === 0 && (
        <div className="mt-6 wofy-card !p-6" data-testid="wc-empty">
          No reports match your filters yet. Change the time range or be the first to file one.
        </div>
      )}
      {!busy && items.length > 0 && (
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="wc-list">
          {items.map((r) => {
            const Icon = REPORT_ICON[r.type] || Cloud;
            const sev = SEVERITY_COLOR[r.severity] || "#6b7280";
            return (
              <Link key={r.id} to={`/weather/report/${r.id}`}
                    className="rounded-2xl border overflow-hidden hover:shadow-md transition"
                    style={{ borderColor: "var(--wofy-border)" }}
                    data-testid={`wc-card-${r.id}`}>
                {r.photo_urls?.[0] ? (
                  <div className="h-40 bg-black/5">
                    <img src={r.photo_urls[0]} alt={r.type} className="w-full h-full object-cover" />
                  </div>
                ) : (
                  <div className="h-40 flex items-center justify-center" style={{ background: `${sev}12` }}>
                    <Icon className="w-14 h-14" style={{ color: sev }} />
                  </div>
                )}
                <div className="p-3">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded"
                          style={{ background: `${sev}22`, color: sev }}>
                      {_titleize(r.severity || "moderate")}
                    </span>
                    <span className="text-sm font-medium">{_titleize(r.type)}</span>
                    {r.verified && (
                      <ShieldCheck className="w-4 h-4 ml-auto" style={{ color: "#16a34a" }}
                                   data-testid={`wc-verified-${r.id}`} />
                    )}
                  </div>
                  <div className="mt-1 text-xs opacity-80 inline-flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {[r.city, r.region, r.country].filter(Boolean).join(", ") || "Somewhere"}
                  </div>
                  {r.description && (
                    <div className="mt-1 text-sm opacity-80 line-clamp-2">{r.description}</div>
                  )}
                  <div className="mt-2 flex items-center justify-between text-[11px] opacity-60">
                    <span>{r.author_name || "Anonymous"}</span>
                    <span>{_ago(r.created_at)}</span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
